/**
 * NeoPayAssurancePage — iter197. Super-admin operational view of the
 * NeoPay mirror retry queue + the daily chain-verifier history.
 *
 * Tile from AdminPage → 'NeoPay Assurance'.
 *
 * Sections:
 *   • At-a-glance health (queue depth, last verify, drift)
 *   • Retry queue table — rows pending or stuck after MAX_RETRY_ATTEMPTS
 *   • Verify-run history — last 30 daily snapshots
 *   • CTAs: "Retry queue now", "Run verifier now"
 */
import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  ArrowLeft, RefreshCw, Loader2, ShieldCheck, ShieldAlert, PlayCircle,
  CircleSlash, ListChecks, Clock, ExternalLink,
} from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '../context/AuthContext';
import api from '../lib/api';
import { Button } from '../components/ui/button';

function formatDateTime(iso) {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleString();
  } catch { return iso; }
}

function Stat({ label, value, sub, testId, tone = 'default' }) {
  const tones = {
    default: 'border-border bg-card',
    good: 'border-emerald-500/40 bg-emerald-500/5',
    warn: 'border-amber-500/40 bg-amber-500/5',
    bad: 'border-red-500/40 bg-red-500/5',
  };
  return (
    <div className={`rounded-xl border-2 px-3 py-2 ${tones[tone]}`} data-testid={testId}>
      <p className="text-xs uppercase tracking-[0.16em] font-bold text-muted-foreground">{label}</p>
      <p className="text-xl font-display font-bold tabular-nums leading-tight mt-0.5">{value}</p>
      {sub && <p className="text-xs text-muted-foreground mt-0.5 truncate">{sub}</p>}
    </div>
  );
}

export default function NeoPayAssurancePage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [queue, setQueue] = useState(null);
  const [runs, setRuns] = useState(null);
  const [loading, setLoading] = useState(true);
  const [retrying, setRetrying] = useState(false);
  const [verifying, setVerifying] = useState(false);
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [q, r] = await Promise.all([
        api.get('/neopay-assurance/queue'),
        api.get('/neopay-assurance/verify-runs?limit=30'),
      ]);
      setQueue(q.data);
      setRuns(r.data);
    } catch (e) {
      setError(e?.response?.data?.detail || 'Failed to load');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const onRetryNow = useCallback(async () => {
    setRetrying(true);
    try {
      const r = await api.post('/neopay-assurance/retry-now', { limit: 200 });
      toast.success(`Retried ${r.data?.retried || 0} · still pending ${r.data?.still_failed || 0} · gave up ${r.data?.gave_up || 0}`);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Retry failed');
    } finally {
      setRetrying(false);
    }
  }, [load]);

  const onVerifyNow = useCallback(async () => {
    setVerifying(true);
    try {
      const r = await api.post('/neopay-assurance/verify-now');
      const total = (r.data?.mismatched || 0) + (r.data?.missing_on_chain || 0);
      if (total > 0) {
        toast.warning(`Drift detected — ${total} transfer(s) need attention`);
      } else {
        toast.success(`Chain clean — ${r.data?.ok || 0} / ${r.data?.checked || 0} verified`);
      }
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Verifier failed');
    } finally {
      setVerifying(false);
    }
  }, [load]);

  if (!user?.is_super_admin) {
    return (
      <div className="min-h-screen p-6 flex flex-col items-center justify-center text-center">
        <ShieldAlert className="w-8 h-8 text-amber-500 mb-3" />
        <p className="text-sm text-muted-foreground" data-testid="assurance-forbidden">
          Super admin access required.
        </p>
        <Button onClick={() => navigate(-1)} className="mt-4">Go back</Button>
      </div>
    );
  }

  const queueRows = queue?.queue || [];
  const lastRun = (runs?.runs || [])[0];
  const stuck = queueRows.filter((r) => r.neopay_retry_gave_up_at).length;
  const pending = queueRows.length - stuck;
  const lastDrift = lastRun ? (lastRun.mismatched || 0) + (lastRun.missing_on_chain || 0) : 0;

  return (
    <div className="min-h-screen pb-24" data-testid="neopay-assurance-page">
      <div className="sticky top-0 z-10 bg-background/90 backdrop-blur border-b border-border px-4 py-3 flex items-center gap-3">
        <button
          type="button"
          onClick={() => navigate(-1)}
          aria-label="Go back"
          data-testid="assurance-back-btn"
          className="p-1 rounded-full hover:bg-muted transition"
        >
          <ArrowLeft className="w-5 h-5" />
        </button>
        <div className="flex-1 min-w-0">
          <h1 className="font-display text-lg font-bold leading-tight">NeoPay Assurance</h1>
          <p className="text-xs text-muted-foreground truncate">Retry queue + daily chain verifier</p>
        </div>
        <button
          type="button"
          onClick={load}
          disabled={loading}
          aria-label="Refresh"
          data-testid="assurance-refresh-btn"
          className="p-2 rounded-full hover:bg-muted transition disabled:opacity-50"
        >
          {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}
        </button>
      </div>

      {error && (
        <div className="m-4 rounded-xl border-2 border-destructive bg-destructive/10 px-3 py-2 text-xs text-destructive" data-testid="assurance-error">
          {error}
        </div>
      )}

      <main className="px-4 py-5 space-y-5">
        {/* Stat strip */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
          <Stat
            label="Pending retry"
            value={pending}
            sub={`max ${queue?.max_attempts ?? 5} attempts`}
            testId="assurance-stat-pending"
            tone={pending > 0 ? 'warn' : 'good'}
          />
          <Stat
            label="Gave up"
            value={stuck}
            sub="needs manual replay"
            testId="assurance-stat-stuck"
            tone={stuck > 0 ? 'bad' : 'good'}
          />
          <Stat
            label="Last verify"
            value={lastRun ? `${lastRun.ok}/${lastRun.checked}` : '—'}
            sub={lastRun ? formatDateTime(lastRun.created_at) : 'never run'}
            testId="assurance-stat-lastverify"
          />
          <Stat
            label="Drift"
            value={lastDrift}
            sub="mismatched + missing"
            testId="assurance-stat-drift"
            tone={lastDrift > 0 ? 'bad' : 'good'}
          />
        </div>

        {/* CTA row */}
        <div className="flex flex-wrap gap-2">
          <Button
            size="sm"
            onClick={onRetryNow}
            disabled={retrying}
            data-testid="assurance-retry-now-btn"
            className="gap-1.5"
          >
            {retrying ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <PlayCircle className="w-3.5 h-3.5" />}
            {retrying ? 'Retrying…' : 'Retry queue now'}
          </Button>
          <Button
            size="sm"
            variant="outline"
            onClick={onVerifyNow}
            disabled={verifying}
            data-testid="assurance-verify-now-btn"
            className="gap-1.5"
          >
            {verifying ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <ShieldCheck className="w-3.5 h-3.5" />}
            {verifying ? 'Verifying…' : 'Run verifier now'}
          </Button>
        </div>

        {/* Queue */}
        <section className="rounded-2xl border-2 border-border bg-card overflow-hidden" data-testid="assurance-queue-section">
          <header className="flex items-center gap-2 px-4 py-3 border-b border-border bg-muted/30">
            <ListChecks className="w-4 h-4 text-primary" />
            <h2 className="font-display text-sm font-bold uppercase tracking-[0.16em] flex-1">Retry queue</h2>
            <span className="text-xs text-muted-foreground">{queueRows.length} row(s)</span>
          </header>
          {queueRows.length === 0 ? (
            <p className="px-4 py-6 text-center text-xs text-muted-foreground" data-testid="assurance-queue-empty">
              Queue is empty · every approved transfer is on the chain.
            </p>
          ) : (
            <div className="divide-y divide-border">
              {queueRows.map((r) => (
                <div key={r.id} className="px-4 py-3 flex items-start gap-3 text-xs" data-testid={`assurance-queue-row-${r.id}`}>
                  <span className={`inline-flex items-center justify-center w-7 h-7 rounded-full ${
                    r.neopay_retry_gave_up_at
                      ? 'bg-red-500/15 text-red-700 dark:text-red-300'
                      : 'bg-amber-500/15 text-amber-700 dark:text-amber-300'
                  }`}>
                    {r.neopay_retry_gave_up_at ? <CircleSlash className="w-3.5 h-3.5" /> : <Clock className="w-3.5 h-3.5" />}
                  </span>
                  <div className="flex-1 min-w-0">
                    <p className="font-semibold truncate">
                      {r.from_user_name || r.from_user_id} → {r.to_user_name || r.to_user_id} · ₹{Math.round(r.amount || 0)}
                    </p>
                    <p className="text-xs text-muted-foreground truncate">
                      {r.neopay_status || 'never_mirrored'} · attempts {r.neopay_retry_count || 0} · {formatDateTime(r.approved_at)}
                    </p>
                  </div>
                  <a
                    href={`/proof/${r.id}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-primary hover:underline inline-flex items-center gap-1 text-xs"
                    data-testid={`assurance-row-cert-${r.id}`}
                  >
                    Cert <ExternalLink className="w-3 h-3" />
                  </a>
                </div>
              ))}
            </div>
          )}
        </section>

        {/* Verify run history */}
        <section className="rounded-2xl border-2 border-border bg-card overflow-hidden" data-testid="assurance-runs-section">
          <header className="flex items-center gap-2 px-4 py-3 border-b border-border bg-muted/30">
            <ShieldCheck className="w-4 h-4 text-primary" />
            <h2 className="font-display text-sm font-bold uppercase tracking-[0.16em] flex-1">Verifier runs</h2>
            <span className="text-xs text-muted-foreground">last 30</span>
          </header>
          {!runs?.runs?.length ? (
            <p className="px-4 py-6 text-center text-xs text-muted-foreground" data-testid="assurance-runs-empty">
              No verifier runs yet. Hit &quot;Run verifier now&quot; to take a baseline snapshot.
            </p>
          ) : (
            <div className="divide-y divide-border">
              {runs.runs.map((run) => {
                const drift = (run.mismatched || 0) + (run.missing_on_chain || 0);
                return (
                  <div key={run.id} className="px-4 py-3 grid grid-cols-12 gap-2 text-xs" data-testid={`assurance-run-${run.id}`}>
                    <p className="col-span-5 font-semibold truncate">{formatDateTime(run.created_at)}</p>
                    <p className="col-span-2 tabular-nums text-emerald-600 dark:text-emerald-400">{run.ok || 0} ok</p>
                    <p className="col-span-2 tabular-nums text-amber-600 dark:text-amber-400">{run.mismatched || 0} mismatch</p>
                    <p className="col-span-2 tabular-nums text-red-600 dark:text-red-400">{run.missing_on_chain || 0} missing</p>
                    <p className="col-span-1 text-right font-semibold">
                      {drift === 0 ? '✓' : '⚠'}
                    </p>
                  </div>
                );
              })}
            </div>
          )}
        </section>
      </main>
    </div>
  );
}
